import {
  UtensilsCrossed,
  DollarSign,
  Users,
  Leaf,
} from 'lucide-react';
import { GenAIModal } from '@/components/ui/genai-modal';
import type { Recipe } from '@/features/recipes';
import type { MenuSuggestion } from './types';

interface MenuSuggestionModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuggestionGenerated: (suggestion: MenuSuggestion) => void;
  readonly availableRecipes: Recipe[];
  readonly venueName?: string;
  readonly menuType?: 'breakfast' | 'lunch' | 'dinner' | 'all-day' | 'special';
}

export function MenuSuggestionModal({
  isOpen,
  onClose,
  onSuggestionGenerated,
  availableRecipes,
  venueName,
  menuType,
}: MenuSuggestionModalProps) {
  const contextBuilder = () => ({
    availableRecipes: availableRecipes.map((r) => ({
      recipeId: r.recipeId,
      name: r.name,
      category: r.category,
      cuisine: r.cuisine,
      allergens: r.allergens,
      dietaryFlags: r.dietaryFlags,
      costPerServing: r.costPerServing,
      difficulty: r.difficulty,
    })),
    venueName,
    preferredMenuType: menuType,
  });

  const renderPreview = (suggestion: MenuSuggestion) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <UtensilsCrossed className="h-5 w-5 text-green-600" />
        <span className="font-medium text-green-800">{suggestion.name}</span>
        {suggestion.theme && (
          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
            {suggestion.theme}
          </span>
        )}
      </div>
      <p className="text-sm text-green-700">{suggestion.description}</p>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700">
          <Users className="h-3 w-3" />
          {suggestion.targetPax} pax
        </span>
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700">
          <UtensilsCrossed className="h-3 w-3" />
          {suggestion.items.length} items
        </span>
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700">
          <DollarSign className="h-3 w-3" />
          Cost: ${suggestion.estimatedFoodCost.toFixed(2)}
        </span>
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700">
          <Leaf className="h-3 w-3" />
          {suggestion.dietaryOptions.vegetarian} vegetarian
        </span>
      </div>
      <div className="text-xs text-green-600">
        {suggestion.items.slice(0, 3).map((item) => item.recipeName).join(', ')}
        {suggestion.items.length > 3 && ` +${suggestion.items.length - 3} more`}
      </div>
    </div>
  );

  return (
    <GenAIModal<MenuSuggestion>
      isOpen={isOpen}
      onClose={onClose}
      onComplete={onSuggestionGenerated}
      promptKey="menu-suggestion"
      title="AI Menu Suggestion"
      description="Describe your menu needs and AI will suggest an optimal composition"
      placeholder="E.g., Create a Mediterranean-themed dinner menu for 120 guests with vegetarian options..."
      contextBuilder={contextBuilder}
      renderPreview={renderPreview}
      submitLabel="Generate Menu"
      successLabel="Use This Menu"
    />
  );
}
